const BYDAY_TO_JS = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
const BYDAY_ORDER = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

/** Per-weekday weekly repeats removed from activity UI; expansion still supports legacy BYDAY rules in DB. */
export const RECUR_WEEKLY_BY_DAY_OPTIONS = [];

/** Weekday chips for Daily repeat (stored as FREQ=DAILY;BYDAY=MO,TU,...). */
export const RECUR_DAILY_BYDAY_OPTIONS = [
  { value: 'MO', label: 'Mon' },
  { value: 'TU', label: 'Tue' },
  { value: 'WE', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' },
  { value: 'SA', label: 'Sat' },
  { value: 'SU', label: 'Sun' },
];

export function parseRrule(rruleStr) {
  if (!rruleStr || typeof rruleStr !== 'string') return { freq: null, byDay: null, byDays: [] };
  const parts = rruleStr.toUpperCase().split(';').map((p) => p.trim()).filter(Boolean);
  let freq = null;
  let byDays = [];
  for (const p of parts) {
    if (p.startsWith('FREQ=')) freq = p.slice(5);
    if (p.startsWith('BYDAY=')) {
      byDays = p
        .slice(6)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return { freq, byDay: byDays[0] || null, byDays };
}

/** Map select value + weekday picks to the rrule string stored in DB. */
export function buildRruleFromFreqAndByDays(freqSelectValue, byDays = []) {
  if (!freqSelectValue) return '';
  if (freqSelectValue === 'FREQ=DAILY' && Array.isArray(byDays) && byDays.length > 0) {
    const normalized = byDays.map((d) => String(d).toUpperCase());
    const sorted = BYDAY_ORDER.filter((d) => normalized.includes(d));
    if (sorted.length > 0) return `FREQ=DAILY;BYDAY=${sorted.join(',')}`;
  }
  return freqSelectValue;
}

/** Normalize a stored rrule to the Repeat dropdown option value. */
export function recurSelectValueFromRrule(rrule) {
  if (!rrule) return '';
  const { freq, byDays } = parseRrule(rrule);
  if (freq === 'DAILY') return 'FREQ=DAILY';
  if (freq === 'WEEKLY' && byDays.length === 1) {
    const full = `FREQ=WEEKLY;BYDAY=${byDays[0]}`;
    if (RECUR_WEEKLY_BY_DAY_OPTIONS.some((o) => o.value === full)) return full;
    return 'FREQ=WEEKLY';
  }
  if (freq === 'WEEKLY') return 'FREQ=WEEKLY';
  if (freq === 'MONTHLY') return 'FREQ=MONTHLY';
  if (freq === 'YEARLY') return 'FREQ=YEARLY';
  return rrule;
}

export function jsWeekdayFromByDay(byDayCode) {
  if (!byDayCode) return undefined;
  return BYDAY_TO_JS[byDayCode.toUpperCase()];
}

/** First instant on or after `anchor` on `targetJsWeekday` (0=Sun … 6=Sat), keeping time-of-day from `anchor`. */
export function firstMatchingWeekdayOnOrAfter(anchor, targetJsWeekday) {
  const d = new Date(anchor.getTime());
  const delta = (targetJsWeekday - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + delta);
  return d;
}

function formatYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Advance by calendar days so wall-clock time is kept across DST (do not add 24h). */
function addLocalCalendarDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/** Inclusive last instant of the local calendar day (rangeTo is usually midnight of that date). */
function atEndOfLocalDay(value) {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
}

function dailyByDaySet(byDays) {
  if (!Array.isArray(byDays) || byDays.length === 0) return null;
  const set = new Set();
  for (const code of byDays) {
    const js = jsWeekdayFromByDay(code);
    if (js !== undefined) set.add(js);
  }
  return set.size > 0 ? set : null;
}

/**
 * Expand a calendar_event_recur rule into display instances (same shape as CenterCalendar / InfoScreen).
 */
export function expandCalendarRecurrence(rule, rangeFrom, rangeTo, { maxInstances = 1000 } = {}) {
  const instances = [];
  const startDate = new Date(rule.start_repeat_date);
  const endDate = atEndOfLocalDay(rule.end_repeat_date);
  const from = new Date(rangeFrom);
  const to = atEndOfLocalDay(rangeTo);

  const effectiveStart = from > startDate ? from : startDate;
  const effectiveEnd = to < endDate ? to : endDate;
  if (effectiveStart > effectiveEnd) return instances;

  const { freq, byDay, byDays } = parseRrule(rule.rrule);
  const dailyDows = freq === 'DAILY' ? dailyByDaySet(byDays) : null;
  let current = new Date(startDate);

  if (freq === 'WEEKLY') {
    const wantDow = jsWeekdayFromByDay(byDay);
    if (wantDow !== undefined) {
      current = firstMatchingWeekdayOnOrAfter(startDate, wantDow);
    }
  }

  let iterations = 0;
  while (current <= effectiveEnd && instances.length < maxInstances && iterations < 20000) {
    iterations += 1;
    const weekdayOk = !dailyDows || dailyDows.has(current.getDay());
    if (current >= effectiveStart && weekdayOk) {
      const dateStr = formatYmd(current);
      instances.push({
        ...rule,
        id: `recur-${rule.id}-${dateStr}`,
        _recur_id: rule.id,
        start_time: new Date(current),
        stop_time: new Date(current),
      });
    }

    if (freq === 'DAILY') {
      current = addLocalCalendarDays(current, 1);
    } else if (freq === 'WEEKLY') {
      current = addLocalCalendarDays(current, 7);
    } else if (freq === 'MONTHLY') {
      const next = new Date(current);
      next.setMonth(next.getMonth() + 1);
      current = next;
    } else if (freq === 'YEARLY') {
      const next = new Date(current);
      next.setFullYear(next.getFullYear() + 1);
      current = next;
    } else {
      break;
    }
  }

  return instances;
}

/**
 * Whether `targetDate` (calendar day) is an occurrence of the recurrence rule.
 */
export function recurRuleFallsOnDate(rule, targetDate) {
  const start = new Date(rule.start_repeat_date);
  const end = new Date(rule.end_repeat_date);
  const target = new Date(targetDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  if (target < start || target > end) return false;

  const { freq, byDay, byDays } = parseRrule(rule.rrule);

  if (freq === 'DAILY') {
    const dailyDows = dailyByDaySet(byDays);
    if (dailyDows && !dailyDows.has(target.getDay())) return false;
    return true;
  }

  if (freq === 'WEEKLY') {
    const wantDow = jsWeekdayFromByDay(byDay);
    if (wantDow !== undefined) {
      if (target.getDay() !== wantDow) return false;
      let cur = firstMatchingWeekdayOnOrAfter(start, wantDow);
      cur.setHours(0, 0, 0, 0);
      let c = 0;
      while (cur <= target && c < 5000) {
        if (cur.getTime() === target.getTime()) return true;
        cur = addLocalCalendarDays(cur, 7);
        c += 1;
      }
      return false;
    }
  }

  const freqStr = (rule.rrule || '').trim();
  let current = new Date(start);
  let count = 0;
  while (current <= target && count < 5000) {
    if (current.getTime() === target.getTime()) return true;
    if (freqStr === 'FREQ=DAILY' || freq === 'DAILY') {
      current = addLocalCalendarDays(current, 1);
    } else if (freqStr === 'FREQ=WEEKLY' || freq === 'WEEKLY') {
      current = addLocalCalendarDays(current, 7);
    } else if (freqStr === 'FREQ=MONTHLY' || freq === 'MONTHLY') {
      const next = new Date(current);
      next.setMonth(next.getMonth() + 1);
      current = next;
    } else if (freqStr === 'FREQ=YEARLY' || freq === 'YEARLY') {
      const next = new Date(current);
      next.setFullYear(next.getFullYear() + 1);
      current = next;
    } else {
      break;
    }
    count += 1;
  }
  return false;
}
